import type { SmokingSession } from '@store/api/sessionApi'

/** Draft with timer running (live smoke). */
export function isActiveSmokingSession(session: SmokingSession): boolean {
  return Boolean(session.isDraft && session.startTime && !session.endTime)
}

/** Draft saved but timer not started — resume in SessionForm. */
export function isResumableDraftSession(session: SmokingSession): boolean {
  return Boolean(session.isDraft && !session.startTime)
}

/** API truth: pick the newest active session if duplicates exist. */
export function pickPrimaryActiveSession(sessions: SmokingSession[]): SmokingSession | null {
  const active = sessions.filter(isActiveSmokingSession)
  if (active.length === 0) return null
  return active.sort((a, b) => {
    const ta = new Date(a.startTime ?? 0).getTime()
    const tb = new Date(b.startTime ?? 0).getTime()
    return tb - ta
  })[0]
}

export function pickPrimaryResumableDraft(sessions: SmokingSession[]): SmokingSession | null {
  const drafts = sessions.filter(isResumableDraftSession)
  if (drafts.length === 0) return null
  return drafts.sort((a, b) => {
    const ta = new Date(a.updatedAt ?? a.createdAt).getTime()
    const tb = new Date(b.updatedAt ?? b.createdAt).getTime()
    return tb - ta
  })[0]
}

export function formatSmokingTimer(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export type SmokingPauseState = {
  accumulatedMs: number
  pausedAt: number | null
}

export function pausedDurationMs(state: SmokingPauseState | undefined, nowMs: number): number {
  if (!state) return 0
  const extra = state.pausedAt != null ? Math.max(0, nowMs - state.pausedAt) : 0
  return state.accumulatedMs + extra
}

export function applyPauseToggle(
  state: SmokingPauseState,
  paused: boolean,
  nowMs: number,
): SmokingPauseState {
  if (paused) {
    if (state.pausedAt != null) return state
    return { ...state, pausedAt: nowMs }
  }
  if (state.pausedAt == null) return state
  return {
    accumulatedMs: state.accumulatedMs + Math.max(0, nowMs - state.pausedAt),
    pausedAt: null,
  }
}

export function elapsedSecondsFromStart(
  startTime: string | undefined,
  nowMs = Date.now(),
  pausedMs = 0,
): number {
  if (!startTime) return 0
  const startMs = new Date(startTime).getTime()
  return Math.max(0, Math.floor((nowMs - startMs - pausedMs) / 1000))
}
